import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup } from 'reactstrap';
import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import clsx from 'clsx';

import MainLayout from '../../layouts/MainLayout';
import useQueryParams from '../../hooks/useQuery';
import { fetchPackages } from '../../api/actions/stripe';
import { fetcher } from '../../api/client';
import { changePlan } from '../../api/actions/profile';
import { useProfile } from '../../api/hooks/profile';
import Title from '../../components/Title';

import Promo from '../../components/Promo';

import { getPrice } from '../../utils';
import config from '../../config';
import { oneTimeProducts, recurringProducts } from './constants';

import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLanguages } from '../../hooks/useLanguages';
import Badge from 'reactstrap/lib/Badge';

function getSize(amount, type = 'mb') {
  if (type === 'tb') {
    return `${amount / 1000000} TB`;
  } else if (type === 'gb') {
    return `${amount / 1000} GB`;
  }
  return `${amount} MB`;
}

function RadioButton({ className, active, onClick, label }) {
  return (
    <div
      className={clsx(
        'form-check py-2 pl-0 border rounded',
        className,
        active && 'active'
      )}
      onClick={onClick}
    >
      <input
        className="form-check-input"
        type="radio"
        value=""
        id=""
        checked={active}
        readOnly
      />
      <label className="form-check-label font-weight-bold" htmlFor="">
        {label}
      </label>
    </div>
  );
}

function PriceCalculator({
  loading,
  packages,
  type,
  currentPackage,
  onPackageChange,
  products,
  currentProduct,
  onProductChange,
  onProductTypeChange,
  onPurchase,
}) {
  const { t } = useTranslation('common');
  const { profile } = useProfile();

  return (
    <div className="card plan-calculator">
      <div className="card-header text-center  bg-light py-3">
        <h3 className="font-weight-bold mb-0">{t('armorPriceCalculator')}</h3>
      </div>
      <ButtonGroup>
        <Button
          color={type === 'one-time' ? 'accent' : 'light'}
          onClick={() => onProductTypeChange('one-time')}
        >
          Un solo pago
        </Button>
        <Button
          color={type === 'recurring' ? 'accent' : 'light'}
          onClick={() => onProductTypeChange('recurring')}
        >
          Suscripciones
        </Button>
      </ButtonGroup>
      <ProductList
        products={products}
        packages={packages}
        hidden={type === 'recurring'}
        currentProduct={currentProduct}
        currentPackage={currentPackage}
        onProductChange={onProductChange}
        onPackageChange={onPackageChange}
      />
      <ProductList
        products={products}
        packages={packages}
        hidden={type === 'one-time'}
        currentProduct={currentProduct}
        currentPackage={currentPackage}
        onProductChange={onProductChange}
        onPackageChange={onPackageChange}
      />
      {(currentProduct.value !== 'starter_plan' ||
        currentPackage.value !== profile.plan?.type) && (
        <div className="card-footer text-center bg-light py-3">
          <Button
            className="text-light w-100"
            color="accent-100"
            onClick={onPurchase}
            disabled={loading}
          >
            {currentProduct.value === 'starter_plan' &&
            currentPackage.value === 'free' &&
            profile.plan?.type !== 'free'
              ? t('Plans.buttons.change')
              : t('actions.buyNow')}
          </Button>
        </div>
      )}
    </div>
  );
}

const benefits = {
  free: [
    'Plantillas gratuitas desarrolladas pon nuestros expertos',
    '25MB de almacenamiento ',
    'Blindaje de Firmas Electrónicas® Nivel 1 - 10',
  ],
  basic: [
    'Todo lo del plan gratuito más',
    'Gestión básica de envío',
    '50GB de almacenamiento',
    'Blindaje de Firmas Electrónicas® Nivel 1 - 10 al mes',
    'Blindaje de Firmas Electrónicas® Nivel 2 - 5 al mes',
  ],
  premium: [
    'Todo lo del plan básico más',
    'Edición ilimitada de elementos',
    '1 plantilla premium al mes gratis',
    '100GB de almacenamiento',
    'Blindaje de Firmas Electrónicas® Nivel 1 - 50 al mes',
    'Blindaje de Firmas Electrónicas® Nivel 2 - 10 al mes',
  ],
};

function StarterPlanItem({
  name,
  active,
  price,
  currency,
  isYourCurrentPlan,
  benefits,
  onClick,
}) {
  return (
    <div
      className={clsx(
        'p-2 border-bottom',
        active && 'border rounded border-accent'
      )}
      onClick={onClick}
    >
      <div className="font-weight-bold text-center">
        {name}
        {isYourCurrentPlan && (
          <Badge className="ml-1" color="accent">
            Plan Actual
          </Badge>
        )}
      </div>

      {/* <ul className="px-1"> */}
      {benefits.map((benefit) => (
        <li>{benefit}</li>
      ))}
      {/* </ul> */}
      <div className="text-center">
        <p className="display-6 mb-0  d-inline-block mb-0 font-weight-bold">
          {price}
        </p>
        <small className="d-inline-block text-muted ml-2">{currency}</small>
      </div>
    </div>
  );
}

function ProductList({
  products,
  hidden,
  packages,
  currentProduct,
  currentPackage,
  onProductChange,
  onPackageChange,
}) {
  const { t } = useTranslation('common');
  const { profile } = useProfile();

  if (hidden) return null;

  return (
    <div className="card-body py-4">
      <div className="pb-4 border-bottom">
        <h5 className="font-weight-bold">{t('selectArmor')}:</h5>
        {products?.map((product, i) => (
          <RadioButton
            key={product.label}
            className={product.length - 1 !== i ? 'mb-3' : ''}
            label={t(product.label, product.localeAttributes)}
            active={currentProduct.value === product.value}
            onClick={onProductChange(product.value)}
          />
        ))}
      </div>
      {currentProduct.value !== 'starter_plan' && (
        <div className="pt-4">
          <h5 className="font-weight-bold">{t('selectAmount')}:</h5>
          <Select
            onChange={onPackageChange}
            options={packages}
            value={currentPackage}
            defaultValue={packages?.[0]}
            className="mb-3"
          />

          <div className="text-center">
            <p className="display-5 mb-0  d-inline-block mb-0 font-weight-bold">
              {currentPackage.price ?? '$0.00'}
            </p>
            <small className="d-inline-block text-muted ml-2">
              {currentPackage?.currency?.toUpperCase() ?? 'MXN'}
            </small>
          </div>
        </div>
      )}
      {currentProduct.value === 'starter_plan' && (
        <div className="mt-2 pt-4">
          {/* <Col> */}
          <StarterPlanItem
            name="Plan Gratuito"
            active={currentPackage.value === 'free'}
            price={'Gratis'}
            isYourCurrentPlan={profile.plan.type === 'free'}
            benefits={benefits.free}
            currency=""
            onClick={() =>
              onPackageChange({
                id: 'free_starter_plan',
                value: 'free',
                label: 'Plan Gratuito (Mensual)',
              })
            }
          />
          <StarterPlanItem
            name="Plan Básico"
            active={currentPackage.value === 'basic'}
            isYourCurrentPlan={profile.plan.type === 'basic'}
            price={packages[1].price ?? '$0.00'}
            benefits={benefits.basic}
            currency={packages[1]?.currency?.toUpperCase() ?? 'MXN'}
            onClick={() => onPackageChange(packages[1])}
          />

          {/* </Col> */}
          {/* <Col> */}
          <StarterPlanItem
            name="Plan Premium"
            active={currentPackage.value === 'premium'}
            isYourCurrentPlan={profile.plan.type === 'premium'}
            price={packages[0].price ?? '$0.00'}
            benefits={benefits.premium}
            currency={packages[0]?.currency?.toUpperCase() ?? 'MXN'}
            onClick={() => onPackageChange(packages[0])}
          />
          {/* </Col> */}
        </div>
      )}
    </div>
  );
}

function getLabel(product, packageObj) {
  const splitPackageName = packageObj.id.split('_');
  const isPlan = packageObj.object === 'plan';
  if (isPlan && product.value === 'storage') {
    return getSize(splitPackageName[0], splitPackageName[1]);
  } else if (isPlan && product.value === 'starter_plan') {
    return splitPackageName[1];
  }
  return splitPackageName[0];
}

export function PriceCalculatorContainer({ title, productId, currency }) {
  const [selectedProduct, setSelectedProduct] = useState(oneTimeProducts[0]);
  const [type, setType] = useState('one-time');
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState(oneTimeProducts);
  const meta = {
    productId: selectedProduct.id,
    type: selectedProduct.type,
    currency,
  };
  const { data = [], isLoading } = useQuery(
    ['store', meta],
    () => fetcher(fetchPackages(meta)),
    {
      onSuccess: (data) => {
        setPackages(
          data?.data
            ?.map((packageObj) => {
              const splitPackageName = packageObj.id.split('_');
              const isPlan = packageObj.object === 'plan';
              return {
                id: packageObj.id,
                type: packageObj.object,
                name: isPlan ? packageObj.nickname : packageObj.attributes.name,
                index: isPlan
                  ? splitPackageName[0]
                  : packageObj.attributes.index,
                label: getLabel(selectedProduct, packageObj),
                value:
                  isPlan && selectedProduct.value === 'starter_plan'
                    ? splitPackageName[1]
                    : splitPackageName[0],
                price: getPrice(
                  packageObj?.price ?? packageObj?.amount,
                  packageObj.currency,
                  false
                ),
                currency: packageObj.currency,
              };
            })
            .sort((a, b) => a.index - b.index)
        );
      },
    }
  );

  const [currentPackage, setCurrentPackage] = useState({});

  const handleChange = (option, action) => {
    setCurrentPackage(option);
  };
  const stripe = useStripe();
  const { profile } = useProfile();
  const queryClient = useQueryClient();

  // useEffect(() => {
  //   console.log({selectedProduct, packages})
  //   if (selectedProduct.id === 'templates') {
  //     setCurrentPackage(packages[0])
  //   }
  // }, [packages, selectedProduct])
  const { mutate: changePlanMutate } = useMutation(
    (payload) => fetcher(changePlan(payload)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('profile');
      },
    }
  );

  const handleChangeProduct = (productId) => () => {
    setSelectedProduct(products.find((opt) => opt.value === productId));
  };

  console.log(currentPackage);
  console.log(currency);
  const handlePurchase = async () => {
    console.log({ currentPackage });
    if (
      selectedProduct.value === 'starter_plan' &&
      currentPackage.value === 'free'
    ) {
      changePlanMutate({ type: 'free' });
    } else {
      await stripe.redirectToCheckout({
        successUrl: `${config.baseUrl}/checkout/success?productId=${
          currentPackage.id
        }&productName=${encodeURIComponent(currentPackage.name)}`,
        cancelUrl: `${config.baseUrl}/shop?currency=${currency}`,
        mode: 'payment',
        lineItems: [
          {
            price: currentPackage.id,
            quantity: 1,
          },
        ],
        clientReferenceId: profile.organization,
        customerEmail: profile.email,
        // locale: 'mx'
      });
    }
  };

  useEffect(() => {
    if (packages) {
      setCurrentPackage(
        (cp) =>
          packages?.find((p) => cp.value === p.value) ?? packages?.[0] ?? {}
      );
    }
  }, [packages]);

  const handleProductTypeChange = (newType) => {
    const newProducts =
      newType === 'one-time' ? oneTimeProducts : recurringProducts;
    setSelectedProduct(newProducts[0]);
    setType(newType);
    setProducts(newProducts);
  };

  return (
    <PriceCalculator
      title={title}
      products={products}
      type={type}
      packages={packages}
      currentPackage={currentPackage}
      onPackageChange={handleChange}
      currentProduct={selectedProduct}
      onProductChange={handleChangeProduct}
      onProductTypeChange={handleProductTypeChange}
      loading={isLoading}
      onPurchase={handlePurchase}
    />
  );
}
